import { ReactNode, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loading } from 'react-daisyui'
import { IconDeviceMobile, IconInfoCircle } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { StatFilters, StatInterval, StatSummaryResponse, StatTimeseriesResponse } from '@/models/stats.ts'
import StatChart, { StatLegend } from './StatChart.tsx'
import { STAT_GROUP_COLORS } from './statColors.ts'

interface StatRange {
  key: string
  label: string
  days: number
  interval: StatInterval
}

const RANGES: StatRange[] = [
  { key: '7', label: '7 Tage', days: 7, interval: 'day' },
  { key: '14', label: '14 Tage', days: 14, interval: 'day' },
  { key: '30', label: '30 Tage', days: 30, interval: 'day' },
  { key: '90', label: '3 Monate', days: 90, interval: 'week' },
  { key: '365', label: '1 Jahr', days: 365, interval: 'month' },
]

/** 'YYYY-MM-DD' in local time — toISOString() would shift the day before 02:00. */
function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function isoDaysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - (days - 1))
  return localIsoDate(date)
}

interface StatsPanelProps {
  /** Empty = everything the account owns. */
  filters?: StatFilters
  /** Extra content below the chart (e.g. the history list on a project). */
  children?: ReactNode
}

/**
 * KPI tiles + chart + full report for one scope. The statistics page and the
 * project tab are the same component, they only differ in `filters`.
 */
export default function StatsPanel({ filters = {}, children }: StatsPanelProps) {
  const { getStatsSummary, getStatsTimeseries } = useClient()
  const [rangeKey, setRangeKey] = useState('14')

  const range = useMemo(() => RANGES.find(r => r.key === rangeKey) ?? RANGES[1], [rangeKey])
  const filterKey = JSON.stringify(filters)
  const query = useMemo(() => ({ ...filters, from: isoDaysAgo(range.days) }), [filterKey, range.days])

  const summaryQuery = useQuery({
    queryKey: ['stats', 'summary', filterKey, range.days],
    queryFn: async () => (await getStatsSummary(query)).data as StatSummaryResponse,
  })

  const timeseriesQuery = useQuery({
    queryKey: ['stats', 'timeseries', filterKey, range.days, range.interval],
    queryFn: async () => (await getStatsTimeseries({ ...query, interval: range.interval })).data as StatTimeseriesResponse,
  })

  const summary = summaryQuery.data
  const timeseries = timeseriesQuery.data
  const isLoading = summaryQuery.isLoading || timeseriesQuery.isLoading
  const isRefreshing = summaryQuery.isFetching || timeseriesQuery.isFetching

  const appInstalls = summary?.primary.find(group => group.key === 'app_installs')?.count ?? 0

  return (
    <div className='flex flex-col gap-5'>
      {/* Time range */}
      <div className='flex flex-wrap gap-2'>
        {RANGES.map(r => (
          <button
            key={r.key}
            onClick={() => setRangeKey(r.key)}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              rangeKey === r.key
                ? 'bg-primary text-neutral border-primary font-medium'
                : 'bg-neutral border-[var(--portal-border)] opacity-70 hover:opacity-100'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className='bg-neutral border border-[var(--portal-border)] p-10 flex justify-center'>
          <Loading color='primary' />
        </div>
      ) : summaryQuery.error || timeseriesQuery.error ? (
        <div className='bg-neutral border border-[var(--portal-border)] p-6'>
          <p className='text-error'>Statistiken konnten nicht geladen werden</p>
        </div>
      ) : (
        <div className={`flex flex-col gap-5 transition-opacity ${isRefreshing ? 'opacity-60' : ''}`}>
          {/* KPI tiles */}
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
            {(summary?.primary ?? []).map(group => (
              <div key={group.key} className='bg-neutral border border-[var(--portal-border)] p-4 md:p-5'>
                <div className='flex items-center gap-2'>
                  <span
                    className='w-2.5 h-2.5 rounded-sm flex-shrink-0'
                    style={{ background: STAT_GROUP_COLORS[group.key] ?? 'var(--portal-border)' }}
                  />
                  <span className='text-sm opacity-60'>{group.label}</span>
                </div>
                <div className='text-3xl font-serif mt-2 tabular-nums'>{group.count}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          {timeseries && (
            <div className='bg-neutral border border-[var(--portal-border)] p-4 md:p-6'>
              <StatChart
                buckets={timeseries.buckets}
                series={timeseries.series}
                interval={timeseries.interval}
              />
              <div className='mt-5 pt-4 border-t border-[var(--portal-border)]'>
                <StatLegend series={timeseries.series} />
              </div>
            </div>
          )}

          {/* App installs split by platform — never compare them unsplit */}
          {appInstalls > 0 && <AppInstallPlatforms filters={filters} days={range.days} />}

          {/* Full report */}
          {summary && (
            <details className='bg-neutral border border-[var(--portal-border)] px-4 md:px-6 py-4' open>
              <summary className='cursor-pointer text-sm opacity-70 hover:opacity-100'>
                Alle Werte im Detail
              </summary>
              <table className='w-full mt-4 text-sm'>
                <tbody>
                  {summary.report.map(row => (
                    <tr key={row.kind} className='border-t border-[var(--portal-border)]'>
                      <td className='py-2 opacity-70'>{row.label}</td>
                      <td className='py-2 text-right font-medium tabular-nums'>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {children}
        </div>
      )}
    </div>
  )
}

/**
 * iOS and Android detect an installation in fundamentally different ways, so the
 * two numbers are only meaningful side by side, each labelled with what it means.
 *
 * - Android: the browser fires a real install event → an actual installation.
 * - iOS: no callback exists; the first launch in standalone mode is the signal →
 *   a lower bound, and dated to that first launch rather than the installation.
 */
function AppInstallPlatforms({ filters, days }: { filters: StatFilters, days: number }) {
  const { getStatsSummary } = useClient()
  const filterKey = JSON.stringify(filters)
  const from = useMemo(() => isoDaysAgo(days), [days])

  const platformQuery = useQuery({
    queryKey: ['stats', 'app-installs', filterKey, days],
    queryFn: async () => {
      const [ios, android, web] = await Promise.all([
        getStatsSummary({ ...filters, from, kind: 'app_install', platform: 'ios' }),
        getStatsSummary({ ...filters, from, kind: 'app_install', platform: 'android' }),
        getStatsSummary({ ...filters, from, kind: 'app_install', platform: 'web' }),
      ])
      return {
        ios: ios.data.total_events,
        android: android.data.total_events,
        web: web.data.total_events,
      }
    },
  })

  const data = platformQuery.data

  return (
    <div className='bg-neutral border border-[var(--portal-border)] p-4 md:p-6'>
      <div className='flex items-center gap-2 mb-4'>
        <IconDeviceMobile size={18} className='text-primary' />
        <h3 className='font-serif text-lg'>App-Installationen nach Plattform</h3>
      </div>

      {platformQuery.isLoading || !data ? (
        <Loading size='sm' color='primary' />
      ) : (
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
          <PlatformTile
            label='iPhone / iPad'
            count={data.ios}
            note='Mindestwert — gezählt beim ersten Öffnen der App'
          />
          <PlatformTile
            label='Android'
            count={data.android}
            note='Tatsächliche Installationen, im Moment der Installation'
          />
          <PlatformTile
            label='Desktop'
            count={data.web}
            note='Installationen über den Browser am Rechner'
          />
        </div>
      )}

      <p className='flex gap-2 text-xs opacity-60 mt-4'>
        <IconInfoCircle size={16} className='flex-shrink-0 mt-px' />
        <span>
          Auf dem iPhone gibt es kein Signal für „Zum Home-Bildschirm“. Eine Installation wird
          erst sichtbar, wenn die App zum ersten Mal geöffnet wird — nie geöffnete Installationen
          fehlen. Die Zahlen sind deshalb nur getrennt aussagekräftig.
        </span>
      </p>
    </div>
  )
}

function PlatformTile({ label, count, note }: { label: string, count: number, note: string }) {
  return (
    <div className='bg-secondary p-4'>
      <div className='text-sm opacity-60'>{label}</div>
      <div className='text-2xl font-serif mt-1 tabular-nums'>{count}</div>
      <div className='text-xs opacity-50 mt-1'>{note}</div>
    </div>
  )
}
